// JOBE-0124: ProjectKindsRail — left-nav for the per-kind project
// entity-browser sub-route (`/projects/{urn}/entities/{kind}`).
//
// Ships the deferred half of Phase 6 Screen C. JOBE-0116 (PR #814) pinned
// each URL to a single kind via `defaultChips` and deferred the rail per
// Decision 2 ("over-spec for current product priority"). This component
// surfaces the rail so users can switch kinds without hand-typing the
// path segment.
//
// Data sources (both already live on `main`):
//   - `EntityTypesContext` (JOBE-0044.6 provider, fed by GET
//     /api/entity-types) — enumeration + display labels + ordering.
//   - `kindCounts` prop (caller passes the project's
//     `counts.entities_by_kind` rollup map from JOBE-0103) — per-kind
//     count badges. Zero-count kinds render dimmed but still clickable
//     so the rail surfaces extensibility (spec §"Approach" Decision).
//
// Ordering: relies on EntityTypesContext, which the BFF
// (`handleListEntityTypes`) already sorts alphabetically by displayName
// — same sibling pattern TypeRailBlock uses. Stable across page loads
// and matches the Library type rail's behaviour.
//
// Active state: each row is a `<NavLink end>` — exact-match active
// class mirrors AdminPage's tab pattern (JOBE-0118).

import React, { useContext } from 'react';
import { NavLink } from 'react-router-dom';

import { EntityTypesContext } from '../library/EntityTypesContext.js';

function Loading() {
  return (
    <ul
      className="project-kinds-rail-list project-kinds-rail-loading"
      aria-busy="true"
      data-testid="project-kinds-rail-loading"
    >
      {[0, 1, 2].map((i) => (
        <li key={i} className="project-kinds-rail-row project-kinds-rail-row-skeleton">
          <span className="skel-line skel-w-80" />
        </li>
      ))}
    </ul>
  );
}

function Empty() {
  return (
    <p
      className="project-kinds-rail-empty muted"
      data-testid="project-kinds-rail-empty"
    >
      No kinds registered.
    </p>
  );
}

function ErrorState() {
  return (
    <p
      className="project-kinds-rail-error muted"
      data-testid="project-kinds-rail-error"
    >
      Couldn&apos;t load kinds.
    </p>
  );
}

export function ProjectKindsRail({ projectURN, activeKind, kindCounts }) {
  const ctx = useContext(EntityTypesContext);
  const counts = kindCounts || {};

  return (
    <nav
      className="project-kinds-rail"
      aria-label="Project kinds"
      data-testid="project-kinds-rail"
    >
      <header className="project-kinds-rail-header">
        <h3 className="project-kinds-rail-title">Kinds</h3>
      </header>
      {ctx.status === 'loading' && <Loading />}
      {ctx.status === 'error' && <ErrorState />}
      {ctx.status === 'ready' && ctx.types.length === 0 && <Empty />}
      {ctx.status === 'ready' && ctx.types.length > 0 && (
        <ul className="project-kinds-rail-list">
          {ctx.types.map((t) => {
            const count = counts[t.kind] || 0;
            const dimmed = count === 0;
            return (
              <li key={t.kind}>
                <NavLink
                  to={`/projects/${encodeURIComponent(projectURN)}/entities/${t.kind}`}
                  // `end` makes the active-state match the path
                  // exactly — same posture as AdminPage's tabs
                  // (JOBE-0118). We also fall back to activeKind for
                  // test environments that mock the router and
                  // don't synthesise location-based active state.
                  end
                  className={({ isActive }) => {
                    const active = isActive || activeKind === t.kind;
                    return [
                      'project-kinds-rail-row',
                      active ? 'project-kinds-rail-row-active' : '',
                      dimmed ? 'project-kinds-rail-row-dimmed' : '',
                    ].filter(Boolean).join(' ');
                  }}
                  data-testid={`project-kinds-rail-row-${t.kind}`}
                >
                  <span className="project-kinds-rail-label">{t.displayName}</span>
                  <span
                    className="project-kinds-rail-count"
                    data-testid={`project-kinds-rail-count-${t.kind}`}
                  >
                    {count}
                  </span>
                </NavLink>
              </li>
            );
          })}
        </ul>
      )}
    </nav>
  );
}
